import { env } from 'env';

import { executeWithTimeout } from './execute-with-timeout';

/**
 * reCAPTCHA Enterprise token minting.
 *
 * Mirrors the widget's implementation in kapa-js
 * (`packages/utils/src/hooks/use-recaptcha.tsx`), against the same site key. The
 * backend rate limits on the sign-in endpoints are keyed on values from the
 * request body, which the caller chooses, so a token is the only part of the
 * request tied to the caller.
 *
 * The site key is a policy-based challenge key: reCAPTCHA shows a captcha on its
 * own when it scores a request low, and `execute()` resolves once the user has
 * answered it.
 */

/**
 * Recaptcha action types to classify recaptcha assessments.
 * IMPORTANT: Make sure these match the ones on the backend.
 */
export enum RecaptchaAction {
  LoginOtpGenerate = 'login_otp_generate',
}

export class RecaptchaUnavailableError extends Error {
  constructor() {
    super('reCAPTCHA verification is unavailable');
    this.name = 'RecaptchaUnavailableError';
  }
}

const RECAPTCHA_TOKEN_HEADER = 'X-Recaptcha-Token';

const RECAPTCHA_SCRIPT_ID = 'kapa-recaptcha-script';

// Loading the script is network-only, so a slow connection is the worst case.
const RECAPTCHA_LOAD_TIMEOUT_MS = 30000;

// Minting shows a challenge when reCAPTCHA scores the request low, so this
// bounds a person solving a captcha rather than a request. Too short and the
// token is dropped while the user is still working on it.
const RECAPTCHA_MINT_TIMEOUT_MS = 120000;

declare global {
  interface Window {
    grecaptcha?: {
      enterprise?: {
        execute: (
          siteKey: string,
          action: { action: string },
        ) => Promise<string>;
        ready: (callback: () => void) => void;
      };
    };
  }
}

let readyPromise: Promise<void> | null = null;

/**
 * Load the reCAPTCHA script once per page and resolve when it can mint tokens.
 */
const loadRecaptcha = (siteKey: string): Promise<void> => {
  readyPromise ??= executeWithTimeout(
    new Promise<void>((resolve, reject) => {
      const onReady = () => {
        const enterprise = window.grecaptcha?.enterprise;
        if (!enterprise) {
          reject(new Error('reCAPTCHA Enterprise did not initialize'));
          return;
        }
        enterprise.ready(() => resolve());
      };

      // There is no flag for "script finished executing", so the presence of the
      // global is what tells us a script tag from an earlier load is ready. A
      // cached script fires no further load event.
      if (window.grecaptcha?.enterprise) {
        onReady();
        return;
      }

      // A script element left over from a failed attempt has already fired its
      // terminal event, so listeners attached now would never run.
      document.getElementById(RECAPTCHA_SCRIPT_ID)?.remove();

      const script = document.createElement('script');
      script.id = RECAPTCHA_SCRIPT_ID;
      script.src = `https://www.recaptcha.net/recaptcha/enterprise.js?render=${siteKey}`;
      script.async = true;
      script.defer = true;
      script.onload = onReady;
      script.onerror = () =>
        reject(new Error('Failed to load reCAPTCHA Enterprise script'));
      document.head.appendChild(script);
    }),
    RECAPTCHA_LOAD_TIMEOUT_MS,
    'Timed out loading the reCAPTCHA script.',
  );

  // A failed attempt is not cached, so a later sign-in retries rather than
  // inheriting one transient failure for the life of the page.
  return readyPromise.catch((error: unknown) => {
    readyPromise = null;
    throw error instanceof Error ? error : new Error(String(error));
  });
};

/**
 * Build the request headers carrying a captcha token for `action`.
 *
 * Returns no header when the site key is unset for local development. When
 * reCAPTCHA is configured, failing here prevents a request that enforcement
 * would reject without explaining that the browser or network blocked it.
 */
export const buildRecaptchaHeaders = async (
  action: RecaptchaAction,
): Promise<Record<string, string>> => {
  const siteKey = env.VITE_RECAPTCHA_SITE_KEY;
  if (!siteKey) return {};

  try {
    await loadRecaptcha(siteKey);
    const token = await executeWithTimeout(
      // Non-null: loadRecaptcha only resolves once this exists.
      window.grecaptcha!.enterprise!.execute(siteKey, { action }),
      RECAPTCHA_MINT_TIMEOUT_MS,
      'Timed out minting a reCAPTCHA token.',
    );
    return { [RECAPTCHA_TOKEN_HEADER]: token };
  } catch (error) {
    console.error('Error obtaining reCAPTCHA token:', error);
    throw new RecaptchaUnavailableError();
  }
};
